import React, { memo, useContext, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { NavLink, useLocation } from 'react-router-dom';
import { ArrowDropDown, ArrowRight } from '@material-ui/icons';
import { context } from '../../../../globalState/GlobalState';
import { isValidString } from '../../../../catalog/Validity';
import { getNavLinkTargetValue } from '../../../../catalog/Website';
import './css/NavItemsMore.css';

// "More ▾" at the end of the desktop menu: holds the items that don't fit in the row. Items with sub-options expand
// in place (any depth) instead of opening side flyouts, so nothing runs off the right edge of the screen.

const getLinkProps = (item) => {
    if (item.value === '/custom') {
        return { external: true, href: item.link, target: getNavLinkTargetValue(item.openNewTab, false) };
    }
    if (item.value === '/custompage' || item.value === '/custompages') {
        return { to: `/pages/${item.link}`, target: getNavLinkTargetValue(item.openNewTab, true) };
    }
    if (item.value === '/forms') {
        return { to: `/forms/${item.link}`, target: getNavLinkTargetValue(item.openNewTab, true) };
    }
    return { to: item.link, target: getNavLinkTargetValue(item.openNewTab, true) };
};

const MoreMenuItem = (props) => {
    const { item, depth, onNavigate } = props;
    const [open, setOpen] = useState(false);
    // Same as the More button: mouse hover expands (a click keeps it open), touch taps toggle.
    const pointerType = useRef('mouse');
    const indent = { paddingLeft: `${25 + (depth * 16)}px` };

    if (item.value === '/options') {
        return (
            // Expands on hover and stays open until More closes: collapsing on mouse-leave shrank the panel out from
            // under the cursor, which then closed the whole popup.
            <div className="nav-more-group" onMouseEnter={() => { setOpen(true); }}>
                <button
                    type="button"
                    className="nav-more-row nav-more-parent"
                    style={indent}
                    aria-expanded={open}
                    onPointerDown={(event) => { pointerType.current = event.pointerType; }}
                    onClick={() => { setOpen(pointerType.current === 'mouse' ? true : !open); }}
                >
                    <span>{item.title}</span>
                    <ArrowRight className={`nav-more-caret${open ? ' nav-more-caret-open' : ''}`} style={{ fontSize: '20px' }} aria-hidden="true" />
                </button>
                {open ? (
                    <div className="nav-more-children">
                        {(item.options || []).map((option) => {
                            return <MoreMenuItem key={option.title} item={option} depth={depth + 1} onNavigate={onNavigate} />;
                        })}
                    </div>
                ) : null}
            </div>
        );
    }

    const link = getLinkProps(item);
    if (link.external) {
        return (
            <a className="nav-more-row" style={indent} href={link.href} target={link.target} rel="noreferrer noopener" onClick={onNavigate}>
                {item.title}
            </a>
        );
    }
    return (
        <NavLink className="nav-more-row" activeClassName="nav-more-row-active" style={indent} to={link.to} exact target={link.target} onClick={onNavigate}>
            {item.title}
        </NavLink>
    );
};

MoreMenuItem.propTypes = {
    item: PropTypes.object.isRequired,
    depth: PropTypes.number.isRequired,
    onNavigate: PropTypes.func.isRequired,
};

const NavItemsMore = (props) => {
    const { items, navItemClassName } = props;
    const { globalState } = useContext(context);
    const [open, setOpen] = useState(false);
    const wrapRef = useRef(null);
    // Mouse users already opened it by hovering, so a click keeps it open; touch taps toggle it.
    const pointerType = useRef('mouse');
    const closeTimer = useRef(null);
    const location = useLocation();

    // Close on route change and on clicks outside the menu.
    useEffect(() => { setOpen(false); }, [location.pathname]);
    useEffect(() => { return () => { clearTimeout(closeTimer.current); }; }, []);
    useEffect(() => {
        if (!open) {
            return undefined;
        }
        const onDown = (event) => {
            if (wrapRef.current && !wrapRef.current.contains(event.target)) {
                setOpen(false);
            }
        };
        const onKey = (event) => {
            if (event.key === 'Escape') {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', onDown);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDown);
            document.removeEventListener('keydown', onKey);
        };
    }, [open]);

    const { colors } = globalState.websiteStyles;

    return (
        <div
            className="nav-more"
            ref={wrapRef}
            style={{ '--nav-more-accent': colors.primaryColor, '--nav-more-on-accent': isValidString(colors.fontColor) ? colors.fontColor : 'white' }}
            // Opens on hover like the other menu items; the panel sits inside this wrapper, so moving into it keeps it open.
            onMouseEnter={() => {
                clearTimeout(closeTimer.current);
                setOpen(true);
            }}
            onMouseLeave={() => {
                // Small grace period so crossing the gap between the button and the panel doesn't close it.
                clearTimeout(closeTimer.current);
                closeTimer.current = setTimeout(() => { setOpen(false); }, 200);
            }}
        >
            <button type="button" className={`nav-item nav-more-button ${navItemClassName}`} aria-haspopup="true" aria-expanded={open} onPointerDown={(event) => { pointerType.current = event.pointerType; }} onClick={() => { setOpen(pointerType.current === 'mouse' ? true : !open); }}>
                More
                <ArrowDropDown style={{ fontSize: '20px' }} />
            </button>
            {open ? (
                <div className="nav-more-panel" role="menu">
                    {items.map((item) => {
                        return <MoreMenuItem key={item.title} item={item} depth={0} onNavigate={() => { setOpen(false); }} />;
                    })}
                </div>
            ) : null}
        </div>
    );
};

NavItemsMore.propTypes = {
    items: PropTypes.array.isRequired,
    navItemClassName: PropTypes.string,
};

NavItemsMore.defaultProps = {
    navItemClassName: '',
};

export default memo(NavItemsMore);
